/**
 * Pure display logic for the Standing Trading Authorization card.
 *
 * WHAT CHRIS SAW (2026-08-21, "didnt work" + screenshot)
 * -----------------------------------------------------
 * An authorization signed Aug 19 with expiry Aug 20 was still rendering, on
 * Aug 21, as "Authorization active" with a green ACTIVE badge, a red "Revoke
 * Standing Authorization" button — and "Days remaining: 0" in the same card.
 *
 * Two defects produced that, both in ./index.jsx:
 *
 *   1. `const saIsActive = standingAuth?.status === 'active'` read the STORED
 *      STATUS STRING and nothing else. Nothing ever rewrites that field — no
 *      server job, no client comparison — so once signed it says 'active'
 *      forever. An expired grant therefore renders as in force forever.
 *
 *   2. Days remaining was `parsed.setHours(16, 0, 0, 0) - Date.now()`.
 *      `setHours` works in the BROWSER's local zone, so a card that prints
 *      "4:00 PM ET" computed its boundary in whatever zone the viewer sits in.
 *      `Math.max(0, ...)` then floored the already-negative result to 0, which
 *      is why the card contradicted its own badge.
 *
 * THE FIX, in one sentence: the card's state is DERIVED from the stored status
 * AND the expiry instant together, in America/New_York, and every part of the
 * card (badge, heading, day count, banner, Revoke button) reads that one value.
 *
 * AGREEING WITH THE SERVER
 * ------------------------
 * functions-ts/src/utils/standingAuthorization.ts decides whether the 4:00 PM ET
 * `cleanupEngagements` job may hold a row: it SKIPS while
 * `calendarDateInNewYork(now) < expiryDate` and deletes on the expiry date
 * itself, when the job fires at 16:00 ET. So the grant is in force right up to
 * 16:00:00 ET on its expiry date and not one second longer.
 *
 * This module mirrors that, evaluated to the second rather than the day:
 *   active  <=>  nowET < `${expiryDate} 16:00:00` (ET wall clock, lexicographic)
 * At every instant the cron can fire, the two halves return the same verdict —
 * see the boundary checks in ./standingAuthDisplay.test.mjs. The ET wall clock
 * comes from Intl.DateTimeFormat with timeZone 'America/New_York', the same
 * mechanism `calendarDateInNewYork` uses server-side, so a viewer in Bangkok and
 * a viewer in New York read the same verdict off the same record.
 * `parseAuthorizationExpiry` is a byte-for-byte mirror of the server's, strict
 * on purpose: a shape the server refuses to honour must not render as active
 * here either.
 *
 * THE WRITE SIDE IS ET TOO
 * ------------------------
 * Fixing only the read side left the record itself timezone-dependent, which is
 * the same class of bug one step earlier:
 *
 *   - the EXPIRY DATE was `new Date()` + `setDate(+ n - 1)`, the browser's
 *     calendar day. Signing at 00:30 in Bangkok — still the previous day in ET —
 *     bought a window one day longer than the identical click from New York,
 *     while enforcement is ET for both. The window a client is granted must not
 *     depend on where the client is sitting.
 *   - the SIGNED STAMP was `toLocaleTimeString` (viewer-local) with the literal
 *     string ' ET' appended. From Bangkok at 9:00 PM local the consent record
 *     read "9:00 PM ET" when the true ET time was 10:00 AM.
 *
 * `standingAuthorizationExpiryForDays` and `standingAuthorizationSignedLabel`
 * below replace both, off `newYorkWallClock` — the same clock the read side and
 * the server use. One mechanism, not three.
 *
 * FAIL CLOSED ON "IS IT IN FORCE" — this REVERSES the fail-open note that stood
 * in this file at commit 2220042, and deliberately. That note applied when the
 * only question was a cosmetic one (does a one-day grant need a Revoke button).
 * Now the same module answers "is this authorization in force", and the honest
 * default for an unreadable record is NOT to claim it is: an unparseable expiry
 * is exactly what the server treats as invalid, so rendering ACTIVE would
 * recreate Chris's complaint. Nothing is lost by it — Revoke lives inside the
 * active card, so a record we cannot read shows the expired card and the
 * "sign a new authorization" form instead, which is the control that helps.
 * The fail-OPEN behaviour survives where it still belongs: an unreadable
 * `signed` on an otherwise valid, unexpired grant keeps the Revoke button.
 *
 * A GRANT THAT IS VOID ON ARRIVAL IS REFUSED, NOT WRITTEN
 * -------------------------------------------------------
 * `standingAuthorizationGrantIsVoid` — see its own note. Signing a 1-day grant
 * after 16:00 ET produced a record that was expired the instant it was stored
 * while the page announced success. The n-1 rule is NOT changed to paper over it
 * (Tin settled that from Chris's history); the page declines and says why.
 *
 * THE BOUNDARY IS ALSO STAMPED ON THE RECORD
 * ------------------------------------------
 * `expiryAt` — Beam's piece from PR #204 (`buildStandingAuthorization`), folded in
 * here at Tin's request. The record now carries the 4:00 PM ET boundary as an ISO
 * instant, read back by `parseExpiryInstant`, with the hand-parsed `expiry` label
 * kept as the fallback for every record written before this. All four readers go
 * through `standingAuthorizationExpiryDate`, so the field that is ENFORCED and the
 * field that is DISPLAYED are the same field.
 *
 * No JSX and no imports, so ./standingAuthDisplay.test.mjs can run it under
 * plain `node` with no framework, matching how frontend checks are done here.
 * (This module and the server one duplicate a small date parser because src/ and
 * functions-ts/ are separate packages with no shared build — kept tiny on
 * purpose, and the test pins the two to the same answers.)
 */

/**
 * Every state the card can be in. Exhaustive: `deriveStandingAuthState` returns
 * one of these and nothing else, so the render sites can switch on it.
 *
 * - ACTIVE   signed, not revoked, expiry readable, and now is before 4:00 PM ET
 *            on that expiry date. The only state that may show Revoke.
 * - EXPIRED  signed but no longer in force: past the boundary, OR a record whose
 *            expiry cannot be read (see FAIL CLOSED above).
 * - REVOKED  the client revoked it. Beats the expiry check.
 * - NONE     no authorization has ever been signed on this profile, or the
 *            stored value is not a usable object.
 */
export const STANDING_AUTH_STATES = Object.freeze({
  ACTIVE: 'active',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
  NONE: 'none',
});

/**
 * The daily engagement clear, and therefore the end of a standing authorization,
 * is 4:00 PM Eastern. Written as an ET wall-clock time-of-day because that is
 * how the card states it and how the server cron is scheduled
 * (`cleanupEngagements`, "0 16 * * *", America/New_York).
 */
export const AUTHORIZATION_EXPIRY_TIME_ET = '16:00:00';

const MONTH_NUMBERS = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

/** Index 1..12 -> the label the account page already prints ('Aug 20, 2026'). */
const MONTH_LABELS = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MS_PER_DAY = 86400000;

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * The day count, as a whole number >= 1.
 *
 * The input is `min={1} step={1}` and already clamped on change; this is the
 * boundary guard, so a junk value yields a one-day grant rather than the literal
 * string 'Invalid Date' on a consent form. Shared by every function that has to
 * agree on what "n days" means — the expiry we write, the expiry we preview, and
 * the sentence that explains the rule to the client.
 */
const clampDays = (days) => {
  const requested = Math.floor(Number(days));
  return Number.isFinite(requested) ? Math.max(1, requested) : 1;
};

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const toCalendarDate = (year, month, day) => {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (year < 2000 || year > 2999) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
};

/**
 * Leading calendar date of a stored standing-auth string, as 'YYYY-MM-DD', or null.
 *
 * Accepts 'Aug 18, 2026' and '2026-08-18', and ignores anything that follows (the
 * `signed` field carries a time and a 'ET' suffix). Parsed by hand rather than with
 * `new Date(str)` so the answer cannot shift with the viewer's timezone.
 *
 * Used for `signed` only. The in-force decision reads `expiry` through
 * `parseAuthorizationExpiry`, which is strict — see there.
 */
export function leadingCalendarDate(value) {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})\b/.exec(raw);
  if (iso) return toCalendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const named = /^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})\b/.exec(raw);
  if (named) {
    const month = MONTH_NUMBERS[named[1].toLowerCase()];
    if (!month) return null;
    return toCalendarDate(Number(named[3]), month, Number(named[2]));
  }

  return null;
}

/**
 * Parse the browser-written `expiry` into 'YYYY-MM-DD', or null.
 *
 * A MIRROR of parseAuthorizationExpiry in
 * functions-ts/src/utils/standingAuthorization.ts, down to the two regexes:
 * whole-string only, two shapes only ('Aug 25, 2026' — what the account page
 * writes — and ISO '2026-08-25'), impossible dates rejected rather than rolled
 * forward the way Date.parse would roll 'Feb 31' into March.
 *
 * Strict on purpose, and stricter than `leadingCalendarDate`: an expiry the
 * server will not honour must not read as in force on this page. Divergence
 * between the two halves is the failure mode this whole feature keeps hitting.
 */
export function parseAuthorizationExpiry(expiry) {
  if (typeof expiry !== 'string') return null;
  const raw = expiry.trim();
  if (!raw) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (iso) return toCalendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const named = /^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(raw);
  if (named) {
    const month = MONTH_NUMBERS[named[1].toLowerCase()];
    if (!month) return null;
    return toCalendarDate(Number(named[3]), month, Number(named[2]));
  }

  return null;
}

/**
 * The instant `now`, as an America/New_York wall clock: 'YYYY-MM-DD HH:MM:SS'.
 *
 * THE POINT OF THIS FUNCTION is that its answer depends only on the instant, not
 * on the machine reading it. `new Date(...).setHours(16, 0, 0, 0)` — what this
 * replaces — depends on the browser's zone, so the same record read from Bangkok
 * and from New York produced different verdicts for a card that says "ET".
 *
 * Fixed-width fields with a fixed separator layout, so string `<` on two of these
 * is chronological order. That is the same trick the server module uses with
 * 'YYYY-MM-DD', extended to the second because the boundary is a time of day.
 */
export function newYorkWallClock(now = new Date()) {
  const instant = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const part = (type) => parts.find((p) => p.type === type)?.value ?? '';
  // Some ICU builds spell midnight '24' under h23; normalise so ordering holds.
  const hour = part('hour') === '24' ? '00' : part('hour');
  return `${part('year')}-${part('month')}-${part('day')} ${hour}:${part('minute')}:${part('second')}`;
}

/**
 * Today's calendar date in America/New_York, 'YYYY-MM-DD'.
 * Same value the server's `calendarDateInNewYork` returns for the same instant.
 */
export function calendarDateInNewYork(now = new Date()) {
  const clock = newYorkWallClock(now);
  return clock === null ? null : clock.slice(0, 10);
}

/**
 * A stored `expiryAt` as a Date, or null.
 *
 * BEAM'S PIECE (PR #204, `buildStandingAuthorization`), folded in here at Tin's
 * request: the record carries the exact 4:00 PM ET boundary as an ISO instant, so
 * a reader no longer has to re-derive it from a HUMAN-READABLE DISPLAY LABEL
 * ('Aug 20, 2026') that exists to be shown to a client.
 *
 * STRICTER THAN `new Date(value)`, on purpose. Beam's version accepted whatever
 * `new Date` accepted; that is the same leniency `parseAuthorizationExpiry` was
 * written to avoid, and this field decides whether consent is in force. So: the
 * exact 24-character shape `Date.prototype.toISOString` emits, and the value must
 * ROUND-TRIP through it. That rejects a rolled-over date ('2026-02-31T20:00:00.000Z'
 * becomes March 3 under Date.parse) and every near-miss string, and it cannot
 * depend on the reader's timezone because the shape is UTC-only.
 *
 * Anything we cannot read falls back to the hand-parsed `expiry` label below —
 * never to "in force".
 */
export function parseExpiryInstant(value) {
  if (typeof value !== 'string') return null;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return instant.toISOString() === value ? instant : null;
}

/**
 * The calendar date, in ET, on which this authorization expires — or null.
 *
 * ONE SOURCE FOR EVERY READER. `expiryAt` (written since this commit) is preferred
 * and the hand-parsed `expiry` label is the fallback for records written before
 * it, exactly as Tin asked. Routing the badge, the printed label, the day count
 * and the in-force decision through this ONE function is what stops a record whose
 * two expiry fields disagree from being ENFORCED off one and DISPLAYED off the
 * other — which is the whole class of defect this branch exists to close.
 */
export function standingAuthorizationExpiryDate(standingAuth) {
  if (!standingAuth || typeof standingAuth !== 'object') return null;
  const stamped = parseExpiryInstant(standingAuth.expiryAt);
  if (stamped !== null) {
    // SAME YEAR BOUND AS THE LABEL PATH — mirrors authorizationExpiryDate in
    // functions-ts/src/utils/standingAuthorization.ts. The ISO shape check accepts
    // any 4-digit year, so expiryAt '9999-12-31T21:00:00.000Z' passed shape and
    // round-trip and rendered as active with ~2.9 million days remaining, while
    // the equivalent label 'Dec 31, 9999' was already refused. users/{uid} is
    // writable by its owner, so this value is not ours to trust.
    const parts = calendarDateInNewYork(stamped).split('-').map(Number);
    return toCalendarDate(parts[0], parts[1], parts[2]);
  }
  return parseAuthorizationExpiry(standingAuth.expiry);
}

/**
 * The exact ET wall-clock instant this authorization stops being in force —
 * '2026-08-20 16:00:00' — or null when the expiry cannot be read.
 */
export function standingAuthorizationExpiresAtEt(standingAuth) {
  const expiryDate = standingAuthorizationExpiryDate(standingAuth);
  return expiryDate === null ? null : `${expiryDate} ${AUTHORIZATION_EXPIRY_TIME_ET}`;
}

/**
 * The UTC instant at which an ET wall clock reads `${calendarDate} ${timeOfDay}`.
 *
 * Solved by iteration against `newYorkWallClock` rather than with a hard-coded
 * -04:00/-05:00 offset, so EDT, EST and any future rule change are all handled by
 * the one mechanism the rest of this module already trusts. The first pass lands
 * within the offset, the second lands exactly; the loop bound is generous and
 * returns null rather than an approximation if it somehow does not converge.
 * (16:00 ET is never inside a DST gap — the transitions are at 02:00 local — so
 * an exact landing always exists for the only time-of-day we ask for.)
 */
function etInstantAt(calendarDate, timeOfDay) {
  const [year, month, day] = calendarDate.split('-').map(Number);
  const [hour, minute, second] = timeOfDay.split(':').map(Number);
  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  let guess = target;
  for (let i = 0; i < 4; i += 1) {
    const clock = newYorkWallClock(new Date(guess));
    if (clock === null) return null;
    if (clock === `${calendarDate} ${timeOfDay}`) return new Date(guess);
    const shown = Date.UTC(
      Number(clock.slice(0, 4)),
      Number(clock.slice(5, 7)) - 1,
      Number(clock.slice(8, 10)),
      Number(clock.slice(11, 13)),
      Number(clock.slice(14, 16)),
      Number(clock.slice(17, 19))
    );
    guess += target - shown;
  }
  return null;
}

/**
 * The `expiryAt` to WRITE for an n-day grant signed at `now` — the ISO instant of
 * 4:00 PM ET on the expiry date, e.g. '2026-08-20T20:00:00.000Z'.
 *
 * Derived from `standingAuthorizationExpiryForDays`, NOT alongside it, so the
 * instant and the label on the same record can never name different days.
 */
export function standingAuthorizationExpiryInstantForDays(days, now = new Date()) {
  const label = standingAuthorizationExpiryForDays(days, now);
  if (label === null) return null;
  const expiryDate = parseAuthorizationExpiry(label);
  if (expiryDate === null) return null;
  const instant = etInstantAt(expiryDate, AUTHORIZATION_EXPIRY_TIME_ET);
  return instant === null ? null : instant.toISOString();
}

/**
 * Would an n-day grant signed RIGHT NOW already be expired the instant it is
 * written? True means the page must refuse to write it.
 *
 * THE DEFECT THIS CLOSES (review HIGH, 2026-08-21). `expiry` is the ET calendar
 * date of signing, advanced by n minus one — see
 * `standingAuthorizationExpiryForDays`, and note that the phrasing here avoids
 * restating that expression, because the mutation testing in
 * ./standingAuthDisplay.test.mjs rewrites it BY TEXT and a comment that echoes it
 * would silently absorb the mutant and leave the real code untouched.
 * The grant ends at 4:00 PM ET on that date. So for n = 1, any signature after
 * 16:00:00 ET produces a record whose boundary is ALREADY IN THE PAST: signing at
 * 21:00 ET wrote expiry 'Aug 19, 2026' and then announced "active until 4:00 PM ET
 * on Aug 19" — five hours earlier — while `deriveStandingAuthState` returned
 * EXPIRED, the day count read 0, Revoke was hidden and the server's
 * `isStandingAuthorizationValid` was false. The page reported success for a grant
 * that was void on arrival.
 *
 * REFUSING IS THE FIX, NOT ROLLING THE EXPIRY FORWARD. Moving the expiry to the
 * next day would change how many days a signature buys, and Tin confirmed from
 * Chris's own history (40d Aug 15 -> Sep 23; 1d Aug 15 -> Aug 15; 10d Aug 16 ->
 * Aug 25; 30d Aug 16 -> Sep 14) that expiry = signature date + n - 1 is the rule
 * and stands. So the n-1 semantics are untouched and the page declines instead.
 *
 * FAILS CLOSED: anything we cannot compute — an unusable clock, an expiry we
 * cannot re-parse — returns true and the signature is declined. Writing a record
 * with a null expiry would be a consent record no reader can honour.
 */
export function standingAuthorizationGrantIsVoid(days, now = new Date()) {
  const expiryLabel = standingAuthorizationExpiryForDays(days, now);
  if (expiryLabel === null) return true;
  const expiryDate = parseAuthorizationExpiry(expiryLabel);
  if (expiryDate === null) return true;
  if (standingAuthorizationExpiryInstantForDays(days, now) === null) return true;
  const nowEt = newYorkWallClock(now);
  if (nowEt === null) return true;
  return !(nowEt < `${expiryDate} ${AUTHORIZATION_EXPIRY_TIME_ET}`);
}

/**
 * The n-day rule in words, for the line beside the day-count input — Tin's
 * suggestion, 2026-08-21: the modal already PREVIEWS the computed expiry, so the
 * rule is disclosed, but "2 days" giving an expiry of tomorrow is not obvious
 * until you read the date. Wording only; nothing here changes what is written.
 *
 * '2 days = today and tomorrow, expiring 4:00 PM ET on Aug 20, 2026.'
 */
export function standingAuthorizationDayCountExplanation(days, now = new Date()) {
  const expiryLabel = standingAuthorizationExpiryForDays(days, now);
  if (expiryLabel === null) return null;
  const n = clampDays(days);
  const span = n === 1
    ? 'today only'
    : n === 2
      ? 'today and tomorrow'
      : `today and the next ${n - 1} days`;
  return `${n} ${n === 1 ? 'day' : 'days'} = ${span}, expiring 4:00 PM ET on ${expiryLabel}.`;
}

/**
 * 'YYYY-MM-DD' -> 'Aug 20, 2026', the shape the account page has always written
 * and printed. Null for anything that is not a calendar date in that form.
 *
 * This is the ONLY place the stored label shape is spelled out, so the value the
 * page WRITES into `expiry` and the value it PRINTS come from the same formatter
 * and `parseAuthorizationExpiry` (and its server mirror) can read back what we
 * wrote. It replaces `formatSaDate`'s `toLocaleDateString('en-US', ...)`, which
 * formatted in the viewer's locale and zone.
 */
export function calendarDateLabel(isoDate) {
  if (typeof isoDate !== 'string') return null;
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  if (!Number.isInteger(year) || !Number.isInteger(day)) return null;
  return `${MONTH_LABELS[month]} ${day}, ${year}`;
}

/**
 * The expiry re-printed from the PARSED date ('Aug 20, 2026'), or null.
 *
 * The card used to echo `standingAuth.expiry` raw. That field is browser-written,
 * so on a record we could not parse the page would print whatever it contained
 * next to a claim about what it means. This returns a label only when the date is
 * one we actually understood; render sites fall back to wording that names no date.
 */
export function standingAuthorizationExpiryLabel(standingAuth) {
  const expiryDate = standingAuthorizationExpiryDate(standingAuth);
  if (expiryDate === null) return null;
  return calendarDateLabel(expiryDate);
}

/**
 * The expiry date to WRITE for an n-day grant signed at `now` — 'Aug 20, 2026'.
 *
 * THE DEFECT THIS REPLACES: ./index.jsx computed this with `new Date()` +
 * `setDate` + `toLocaleDateString`, i.e. the BROWSER's calendar day. A client
 * signing at 00:30 in Bangkok is still on the previous ET day, so they were
 * granted an expiry one day later than a New York client signing at the very
 * same instant — while enforcement is ET on both halves (this module and the
 * 4:00 PM ET `cleanupEngagements` cron). The length of window a client receives
 * must not depend on where they are sitting, so the day count starts from the
 * ET calendar day, exactly like every other date in this feature.
 *
 * N-DAY SEMANTICS ARE PRESERVED EXACTLY, deliberately: `n - 1` means "n calendar
 * days COUNTING TODAY", so a 2-day authorization signed on the 19th expires at
 * 4:00 PM ET on the 20th. That is what Chris's live record says (signed Aug 19
 * 12:39 PM ET, expiry Aug 20) and it is what `daysRemainingInAuthorization`
 * counts back. Whether a client reading "2 days" expects two FULL days is an open
 * product question with Chris and is NOT settled here; this function only makes
 * the existing rule explicit and the test below pins it, so that changing it
 * later is a deliberate decision rather than an accident.
 *
 * `days` is clamped by `clampDays` above. Returns null only when `now` itself is
 * unusable.
 */
export function standingAuthorizationExpiryForDays(days, now = new Date()) {
  const todayEt = calendarDateInNewYork(now);
  if (todayEt === null) return null;
  const n = clampDays(days);
  const [year, month, day] = todayEt.split('-').map(Number);
  // Pure calendar arithmetic in UTC: no DST shift can move the day across, which
  // is the whole reason this is not `new Date()` + `setDate`.
  const shifted = new Date(Date.UTC(year, month - 1, day + (n - 1)));
  return calendarDateLabel(
    `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`
  );
}

/**
 * The `signed` stamp to WRITE — 'Aug 19, 2026 12:39 PM ET' — in real ET, or null
 * when `now` is unusable.
 *
 * THE DEFECT THIS REPLACES: ./index.jsx formatted the viewer's LOCAL time with
 * `toLocaleTimeString` and then appended the literal string ' ET'. Signing from
 * Bangkok at 9:00 PM local wrote "9:00 PM ET" when the true ET time was 10:00 AM.
 * This is the timestamp on a signed consent record, so it has to be the time it
 * claims to be.
 *
 * Built from `newYorkWallClock` rather than a second `Intl` call with hour12, so
 * the stamp, the expiry and the in-force decision all read one clock — and so the
 * separator before AM/PM is a plain space, not the narrow no-break space some ICU
 * builds emit for `hour12: true`.
 */
export function standingAuthorizationSignedLabel(now = new Date()) {
  const clock = newYorkWallClock(now);
  if (clock === null) return null;
  const dateLabel = calendarDateLabel(clock.slice(0, 10));
  if (dateLabel === null) return null;
  const hour24 = Number(clock.slice(11, 13));
  const minute = clock.slice(14, 16);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const meridiem = hour24 < 12 ? 'AM' : 'PM';
  return `${dateLabel} ${hour12}:${minute} ${meridiem} ET`;
}

/** Trim + lowercase, or '' for anything that is not a string. */
const normalizeStatus = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/**
 * The card's state, from the stored status AND the expiry together.
 *
 * Order matters: revoked is checked before the clock, because a revoked grant is
 * revoked whether or not its expiry has also passed, and the card should say so.
 * Anything that is not a recognised, in-force, readable grant lands on EXPIRED or
 * NONE — never ACTIVE. This is the single value the whole card renders from, so
 * the badge, the heading, the day count and the banner cannot disagree.
 */
export function deriveStandingAuthState(standingAuth, now = new Date()) {
  if (!standingAuth || typeof standingAuth !== 'object' || Array.isArray(standingAuth)) {
    return STANDING_AUTH_STATES.NONE;
  }
  const status = normalizeStatus(standingAuth.status);
  if (status === 'revoked') return STANDING_AUTH_STATES.REVOKED;
  // A STORED 'expired' is now a real value: the 4 PM ET cleanup writes it back
  // onto a stale active record (Beam's expireStandingAuthIfNeeded, folded into
  // functions-ts/src/utils/standingAuthorization.ts). Without this line such a
  // record would fall to NONE, and the card would tell a client who signed last
  // week that they have never signed anything, instead of showing the prior
  // authorization and why it ended. It is still not ACTIVE, so nothing fails open.
  if (status === 'expired') return STANDING_AUTH_STATES.EXPIRED;
  if (status !== 'active') return STANDING_AUTH_STATES.NONE;

  const expiresAt = standingAuthorizationExpiresAtEt(standingAuth);
  // Unreadable expiry: we cannot show it is in force, so we do not say it is.
  if (expiresAt === null) return STANDING_AUTH_STATES.EXPIRED;

  const nowEt = newYorkWallClock(now);
  if (nowEt === null) return STANDING_AUTH_STATES.EXPIRED;

  return nowEt < expiresAt ? STANDING_AUTH_STATES.ACTIVE : STANDING_AUTH_STATES.EXPIRED;
}

/**
 * Whole days this authorization still covers, COUNTING TODAY. 0 unless active.
 *
 * "Counting today" is the definition that matches the rest of the feature rather
 * than fighting it:
 *   - `standingAuthorizationExpiryForDays(n)` writes expiry = todayET + (n - 1),
 *     so on the day of signing this returns exactly the n the client chose.
 *   - the grant runs to 4:00 PM ET on the expiry date, so on that last day there
 *     is still a trading day left to count, and this returns 1 — never 0.
 * That last point is the contradiction Chris photographed: while the card says
 * ACTIVE this function cannot return 0, because active means nowET is before
 * 16:00 ET on the expiry date, which means todayET <= expiryDate.
 *
 * Pass the SAME `now` used for `deriveStandingAuthState`. Two `new Date()` reads
 * a millisecond apart could straddle the boundary and reintroduce exactly the
 * "ACTIVE / 0 days" pairing this replaces.
 */
export function daysRemainingInAuthorization(standingAuth, now = new Date()) {
  if (deriveStandingAuthState(standingAuth, now) !== STANDING_AUTH_STATES.ACTIVE) return 0;
  const expiryDate = standingAuthorizationExpiryDate(standingAuth);
  const todayEt = calendarDateInNewYork(now);
  // Unreachable while ACTIVE (that state proves both parsed), but this function is
  // exported and must be total rather than throw into a render.
  if (expiryDate === null || todayEt === null) return 0;
  const utcMs = (date) => {
    const [year, month, day] = date.split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };
  return Math.round((utcMs(expiryDate) - utcMs(todayEt)) / MS_PER_DAY) + 1;
}

/**
 * True only when we can read BOTH dates and they are the same calendar day —
 * i.e. this authorization expires on the day it was signed.
 *
 * Chris, 2026-08-17, item 1. A one-day grant ends at 4:00 PM ET on the day it was
 * signed and the 4 PM ET cleanup clears that engagement anyway, so the button
 * offers to stop something that stops by itself, and the confirm dialog's promise
 * ("your account returns to daily engagement today") is already true unpressed.
 *
 * Fails open by itself (unreadable => false => keep the button). That still holds
 * for an unreadable `signed`; an unreadable `expiry` no longer reaches here,
 * because such a record is not ACTIVE and the button is not rendered at all.
 */
export function isSameDayAuthorization(standingAuth) {
  if (!standingAuth || typeof standingAuth !== 'object') return false;
  const signedDate = leadingCalendarDate(standingAuth.signed);
  const expiryDate = leadingCalendarDate(standingAuth.expiry);
  if (!signedDate || !expiryDate) return false;
  return signedDate === expiryDate;
}

/**
 * Should the "Revoke Standing Authorization" button render?
 *
 * Only while there is something to revoke: the authorization is genuinely in
 * force AND it is not a same-day grant that ends by itself at 4:00 PM ET today.
 * Chris asked for this twice — item 1 of his 8/17 list, and again in the 8/21
 * screenshot where the button sat under an expired grant.
 */
export function showRevokeStandingAuthorization(standingAuth, now = new Date()) {
  if (deriveStandingAuthState(standingAuth, now) !== STANDING_AUTH_STATES.ACTIVE) return false;
  return !isSameDayAuthorization(standingAuth);
}
